/**
 * Client-side dedup so a flapping widget or an outage doesn't flood the error-log
 * API with hundreds of identical reports. Fingerprints are scoped to this browser
 * session only — it's a throttle, not a backend-wide dedup.
 */
const DEFAULT_THROTTLE_WINDOW_MS = 15_000;

const lastSeenAt = new Map<string, number>();

function hashString(value: string): string {
  let hash = 5381;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 33) ^ (value.codePointAt(index) ?? 0);
  }
  return (hash >>> 0).toString(36);
}

export function computeErrorFingerprint(
  parts: Array<string | number | null | undefined>
): string {
  return hashString(parts.map((part) => String(part ?? '')).join('|'));
}

/**
 * Returns true when this fingerprint was already logged within the throttle window
 * and should be skipped. Also records the current attempt as "seen" as a side effect.
 */
export function shouldThrottleErrorLog(
  fingerprint: string,
  windowMs: number = DEFAULT_THROTTLE_WINDOW_MS
): boolean {
  const now = Date.now();
  const previous = lastSeenAt.get(fingerprint);

  if (previous !== undefined && now - previous < windowMs) {
    return true;
  }

  lastSeenAt.set(fingerprint, now);
  return false;
}

export function resetErrorFingerprintThrottle(): void {
  lastSeenAt.clear();
}
